import { ref, computed, watchEffect, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

/**
 * v6.4.66: Composable for dynamic sidebar styling
 * Dual-layer: Global (InstallationConfig PLATFORM_STYLE) + Per-Account (custom_attributes.sidebar_style)
 * Account style overrides global. If neither exists, returns null (use system defaults).
 *
 * Bug fixes (v6.4.66b):
 * - isDarkMode is now tracked via a reactive ref + MutationObserver (was non-reactive in computed)
 * - globalStyle parsed once at init (window.globalConfig is static after page load)
 * - MutationObserver properly triggers reactive updates via ref
 */

const STYLE_KEYS = [
  'sidebar_bg',
  'menu_btn_bg',
  'menu_btn_text',
  'menu_btn_active_bg',
  'menu_btn_active_text',
  'submenu_bg',
  'submenu_text',
  'icon_color',
  'hover_bg',
];

// Map style keys to CSS custom property names
const CSS_VAR_MAP = {
  sidebar_bg: '--sidebar-bg',
  menu_btn_bg: '--sidebar-menu-btn-bg',
  menu_btn_text: '--sidebar-menu-btn-text',
  menu_btn_active_bg: '--sidebar-menu-btn-active-bg',
  menu_btn_active_text: '--sidebar-menu-btn-active-text',
  submenu_bg: '--sidebar-submenu-bg',
  submenu_text: '--sidebar-submenu-text',
  icon_color: '--sidebar-icon-color',
  hover_bg: '--sidebar-hover-bg',
};

/**
 * Detect if the current theme is dark mode.
 * IMPORTANT: themeHelper.js toggles .dark on document.body (NOT document.documentElement).
 * We also check the color-scheme CSS property on <html> as a secondary signal.
 * We do NOT use prefers-color-scheme because it reflects the OS preference,
 * not the user's in-app choice (user may pick light theme on a dark OS).
 */
function detectDarkMode() {
  // Primary: themeHelper.js adds/removes .dark on <body>
  if (document.body?.classList.contains('dark')) return true;
  // Secondary: themeHelper.js sets color-scheme on <html>
  const colorScheme = document.documentElement.style.getPropertyValue('color-scheme');
  if (colorScheme === 'dark') return true;
  return false;
}

/**
 * Parse PLATFORM_STYLE from globalConfig.
 * The value may be a JSON string or already an object.
 * Note: globalConfig is static after page load, so this only needs to be called once.
 */
function parseGlobalStyle() {
  try {
    const raw = window.globalConfig?.PLATFORM_STYLE;
    if (!raw) return null;

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function useSidebarStyle() {
  const store = useStore();
  const route = useRoute();

  // FIX #1: Make dark mode detection reactive via ref
  // This ref gets mutated by MutationObserver and matchMedia listener,
  // which correctly triggers computed re-evaluation.
  const isDark = ref(detectDarkMode());

  // FIX #5: Parse globalConfig once at init (it's static after page load)
  // Using ref instead of computed prevents unnecessary re-evaluation
  const globalStyle = ref(parseGlobalStyle());

  // Get account-level sidebar_style from the accounts store
  const accountStyle = computed(() => {
    const accountId = route.params?.accountId;
    if (!accountId) return null;

    const account = store.getters['accounts/getAccount'](accountId);
    if (!account || typeof account !== 'object') return null;

    const style = account.custom_attributes?.sidebar_style;
    return style && typeof style === 'object' ? style : null;
  });

  // Merged style: account overrides global
  const effectiveStyle = computed(() => {
    const acct = accountStyle.value;
    const global = globalStyle.value;

    // Priority: account > global > null
    const style = acct || global;
    if (!style || !style.enabled) return null;

    return style;
  });

  // Is custom style active?
  const isCustomStyleActive = computed(() => effectiveStyle.value !== null);

  // Current theme colors (based on dark/light mode)
  // FIX #1: isDark.value is now a reactive dependency that correctly triggers
  // re-evaluation when the MutationObserver detects class/attribute changes.
  const currentThemeColors = computed(() => {
    const style = effectiveStyle.value;
    if (!style) return null;

    const theme = isDark.value ? 'dark' : 'light';
    const colors = style[theme];
    return colors && typeof colors === 'object' ? colors : null;
  });

  // CSS variables object for :style binding on the sidebar container
  const sidebarStyleVars = computed(() => {
    const colors = currentThemeColors.value;
    if (!colors) return {};

    const vars = {};
    STYLE_KEYS.forEach(key => {
      const value = colors[key];
      const cssVar = CSS_VAR_MAP[key];
      if (value && cssVar && typeof value === 'string') {
        vars[cssVar] = value;
      }
    });
    return vars;
  });

  // Inject CSS variables into :root for global access (needed by child components)
  watchEffect(() => {
    const colors = currentThemeColors.value;
    const root = document.documentElement;

    if (colors) {
      root.setAttribute('data-sidebar-custom', 'true');
      STYLE_KEYS.forEach(key => {
        const value = colors[key];
        const cssVar = CSS_VAR_MAP[key];
        if (value && cssVar && typeof value === 'string') {
          root.style.setProperty(cssVar, value);
        }
      });
    } else {
      root.removeAttribute('data-sidebar-custom');
      STYLE_KEYS.forEach(key => {
        if (CSS_VAR_MAP[key]) {
          root.style.removeProperty(CSS_VAR_MAP[key]);
        }
      });
    }
  });

  // Listen for OS-level prefers-color-scheme changes
  const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
  const themeChangeHandler = () => {
    isDark.value = detectDarkMode();
  };
  mediaQuery?.addEventListener?.('change', themeChangeHandler);

  // Observe class changes on <body> for .dark toggle (themeHelper.js toggles .dark on body)
  const observer = new MutationObserver(() => {
    isDark.value = detectDarkMode();
  });
  if (document.body) {
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }
  // Also observe <html> for color-scheme style changes
  const htmlObserver = new MutationObserver(() => {
    isDark.value = detectDarkMode();
  });
  htmlObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style'],
  });

  onUnmounted(() => {
    mediaQuery?.removeEventListener?.('change', themeChangeHandler);
    observer.disconnect();
    htmlObserver.disconnect();
    // Clean up CSS variables
    const root = document.documentElement;
    root.removeAttribute('data-sidebar-custom');
    STYLE_KEYS.forEach(key => {
      if (CSS_VAR_MAP[key]) {
        root.style.removeProperty(CSS_VAR_MAP[key]);
      }
    });
  });

  return {
    isCustomStyleActive,
    sidebarStyleVars,
    currentThemeColors,
    effectiveStyle,
  };
}
